import styled from "@emotion/styled";
import { isHotkey } from "is-hotkey";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { Editor, Element, Point, Range, RangeRef, Transforms } from "slate";
import { ReactEditor, useFocused, useSlate } from "slate-react";

import { CssTooltip } from "@livechat/design-system";
import { DesignToken, RadiusToken } from "@livechat/design-system-react-components";

import { ResponsiveBreakpoint, ResponsiveBreakpointType } from "constants/responsive-breakpoint";
import { StackingContextLevel } from "styles/stacking-context-level";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import LinkForm from "./form";
import LinkToolbar from "./toolbar";

export interface IUpdateParams {
  selection: RangeRef | null;
  focusDisplayName?: boolean;
}

const LinkUI: React.FC<{
  params: IUpdateParams | null;
  setParams: React.Dispatch<React.SetStateAction<IUpdateParams | null>>;
  containerRef: HTMLElement | null;
  eventPlace?: string;
  onInit?: (positionUI: () => void) => () => void;
}> = ({ params: updateParams, setParams: setUpdateParams, containerRef, eventPlace, onInit }) => {
  const editor = useSlate();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [insideLink, setInsideLink] = useState(false);
  const [portalCreated, togglePortalCreated] = useState(false);
  const target = useRef<RangeRef | null>(null);

  const isFocus = useFocused();

  const createPortal = useCallback(() => {
    const newPortal = document.createElement("div");
    newPortal.className = "link-ui-portal";
    document.body.appendChild(newPortal);
    togglePortalCreated(true);
  }, []);

  const destroyPortal = useCallback(() => {
    const toRemovePortal = document.querySelector(".link-ui-portal");
    // @ts-expect-error TS(2345): Argument of type 'Element | null' is not assignabl... Remove this comment to see the full error message
    document.body.removeChild(toRemovePortal);
  }, []);

  const positionUI = useCallback(() => {
    const targetRange = target.current;
    const tooltip = tooltipRef.current;

    const testOffset = targetRange &&
      targetRange.current && {
        focus: {
          path: targetRange.current.focus.path,
          offset: 0,
        },
        anchor: {
          path: targetRange.current.anchor.path,
          offset: 0,
        },
      };

    if (tooltip && targetRange && targetRange.current) {
      // @ts-expect-error TS(2345)
      const domRange = ReactEditor.toDOMRange(editor, testOffset);
      const tooltipBox = tooltip.getBoundingClientRect();
      const rangeBox = domRange.getBoundingClientRect();

      const leftCorrection = Math.max(0, rangeBox.left + tooltipBox.width - window.innerWidth);

      tooltip.style.left = `${Math.max(0, rangeBox.left - leftCorrection) + window.scrollX}px`;
      tooltip.style.top = `${Math.max(0, rangeBox.top - tooltipBox.height) + window.scrollY}px`;

      const isDesktop = window.matchMedia(
        `(min-width: ${ResponsiveBreakpoint[ResponsiveBreakpointType.Mobile]}px)`
      ).matches;

      const arrow = tooltip.querySelector<HTMLDivElement>(".lc-css-tooltip__arrow");
      if (isDesktop) {
        tooltip.style.left = `${Math.max(0, rangeBox.left - leftCorrection) + window.scrollX}px`;
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        arrow.style.left = "8px";
      } else {
        tooltip.style.left = "8px";
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        arrow.style.left = `${Math.max(rangeBox.left + window.pageXOffset - leftCorrection - 8)}px`;
      }
    }
  }, [editor]);

  const closeUI = useCallback(
    (restoreFocus = true) => {
      const [linkElement] = Editor.nodes(editor, {
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        match: (node: any) => Element.isElement(node) && node.type === "link",
      });

      // @ts-expect-error TS(18047)
      if (restoreFocus && (linkElement || (updateParams && updateParams.selection.current))) {
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        const end = linkElement ? Editor.end(editor, linkElement[1]) : Range.end(updateParams.selection.current);

        Editor.withoutNormalizing(editor, () => {
          if (!ReactEditor.isFocused(editor)) {
            ReactEditor.focus(editor);
          }
          Transforms.select(editor, end);
        });
      }
      setUpdateParams(null);
      setInsideLink(false);
    },
    [editor, updateParams, setUpdateParams]
  );

  const closeOnEsc = useCallback(
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      if (isHotkey("esc")(event)) {
        event.preventDefault();
        event.stopPropagation();
        closeUI();
      }
    },
    [closeUI]
  );

  const closeOnOutsideClick = useCallback(
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      if (
        containerRef &&
        tooltipRef.current &&
        !(containerRef.contains(event.target) || tooltipRef.current.contains(event.target))
      ) {
        closeUI(false);
      }
    },
    [closeUI, containerRef]
  );

  const handleEdit = useCallback(
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      event.preventDefault();

      const [linkEntry] = Editor.nodes(editor, {
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        match: (node: any) => Element.isElement(node) && node.type === "link",
        universal: true,
      });

      if (linkEntry) {
        const path = linkEntry[1];
        const linkRange = Editor.range(editor, Editor.start(editor, path), Editor.end(editor, path));
        Transforms.select(editor, Editor.range(editor, Editor.start(editor, path), Editor.end(editor, path)));
        setUpdateParams({
          selection: Editor.rangeRef(editor, linkRange, { affinity: "inward" }),
          focusDisplayName: true,
        });
      }
    },
    [editor, setUpdateParams]
  );

  const handleUnlink = useCallback(
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      event.preventDefault();

      const [linkEntry] = Editor.nodes(editor, {
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        match: (node: any) => Element.isElement(node) && node.type === "link",
        universal: true,
      });

      if (linkEntry) {
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Transforms.unwrapNodes(editor, { match: (node: any) => Element.isElement(node) && node.type === "link" });
      }
    },
    [editor]
  );

  useEffect(() => {
    const { selection } = editor;

    if (!isFocus) {
      if (target.current && insideLink && !updateParams) {
        target.current.unref();
        target.current = null;
      } else {
        setInsideLink(false);
        return;
      }
    }

    const [linkEntry] = Editor.nodes(editor, {
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      match: (node: any) => Element.isElement(node) && node.type === "link",
      universal: true,
      // @ts-expect-error TS(18047)
      ...(updateParams && updateParams.selection.current ? { at: updateParams.selection.current } : {}),
    });

    if (linkEntry) {
      const isPastLink =
        // @ts-expect-error TS(2345)
        Range.isCollapsed(selection) && Point.equals(selection.anchor, Editor.end(editor, linkEntry[1]));
      const linkSelection = Editor.range(editor, Editor.start(editor, linkEntry[1]), Editor.end(editor, linkEntry[1]));

      if (!isPastLink) {
        target.current = Editor.rangeRef(editor, linkSelection);
        setInsideLink(true);
      } else {
        setInsideLink(false);
        setUpdateParams(null);
      }
    } else {
      setInsideLink(false);
    }

    // @ts-expect-error TS(18047)
    if (updateParams !== null && !updateParams.selection.current) {
      setUpdateParams(null);
    }

    if (
      editor.selection &&
      updateParams &&
      // @ts-expect-error TS(18047)
      updateParams.selection.current &&
      // @ts-expect-error TS(18047)
      !Range.equals(updateParams.selection.current, editor.selection)
    ) {
      setUpdateParams(null);
    }
  }, [editor, editor.selection, setUpdateParams, updateParams, isFocus, insideLink]);

  useEffect(() => {
    createPortal();
    return destroyPortal;
  }, [createPortal, destroyPortal]);

  useEffect(() => {
    document.addEventListener("keydown", closeOnEsc);
    document.addEventListener("click", closeOnOutsideClick);
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let cleanup: any = null;
    if (onInit) {
      cleanup = onInit(positionUI);
    }

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      document.removeEventListener("keydown", closeOnEsc);
      document.removeEventListener("clock", closeOnOutsideClick);
      if (cleanup) {
        cleanup();
      }
    };
  }, [closeOnEsc, closeOnOutsideClick, onInit, positionUI]);

  useLayoutEffect(() => {
    if (updateParams === null && !insideLink && target.current) {
      target.current.unref();
      target.current = null;
    }

    // @ts-expect-error TS(18047)
    if (updateParams !== null && target.current === null && updateParams.selection.current) {
      target.current = updateParams.selection;
    }
  }, [insideLink, updateParams]);

  useLayoutEffect(() => {
    const timeout = setTimeout(positionUI, 0);
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => clearTimeout(timeout);
  });

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [linkEntry] = Editor.nodes(editor, { match: (node: any) => Element.isElement(node) && node.type === "link" });
  const isVisible = !!(
    // @ts-expect-error TS(18047)
    ((updateParams !== null && updateParams.selection.current) || (insideLink && updateParams === null && linkEntry))
  );

  return portalCreated
    ? ReactDOM.createPortal(
        <div style={{ position: "absolute", zIndex: StackingContextLevel.Tooltip }} ref={tooltipRef}>
          <TooltipContainer>
            <LinkUIContainer style={{ display: isVisible ? "block" : "none" }}>
              <CssTooltip isVisible={isVisible} placement="top-start" theme="invert">
                {updateParams !== null && updateParams?.selection?.current && (
                  <LinkForm params={updateParams} eventPlace={eventPlace} closeUI={closeUI} />
                )}
                {insideLink && updateParams === null && (
                  <LinkToolbar handleEdit={handleEdit} handleUnlink={handleUnlink} />
                )}
              </CssTooltip>
            </LinkUIContainer>
          </TooltipContainer>
        </div>,
        // @ts-expect-error TS(2345): Argument of type 'Element | null' is not assignabl... Remove this comment to see the full error message
        document.querySelector(".link-ui-portal")
      )
    : null;
};

const TooltipContainer = styled.div`
  height: 100%;
  overflow: visible;
  z-index: ${StackingContextLevel.Tooltip};

  .lc-css-tooltip {
    max-width: 360px;
    background-color: var(${DesignToken.TooltipBackgroundBasic});
    border-radius: var(${RadiusToken.Radius3});
  }

  .lc-css-tooltip__arrow {
    border-color: var(${DesignToken.TooltipBackgroundBasic});
  }

  @media (max-width: ${ResponsiveBreakpoint[ResponsiveBreakpointType.Mobile]}px) {
    .lc-css-tooltip {
      max-width: 100vw;
    }
  }
`;

const LinkUIContainer = styled.div`
  position: relative;

  a p {
    color: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.linkColor
    };
    text-decoration: none;
  }
`;

export default LinkUI;
